<script setup lang="ts">
import type { PropType } from 'vue';

import {
    BackendErrorCode,
    type BackendErrorEntity,
    DirectSaleComputedState,
    DirectSaleState,
    type ErrorType,
    NegotiationState,
    type OrderEntity,
    useApiPlaceOrderDirectSale,
    type DirectSaleItemEntity,
} from '~/apiClient';
import type { DsdpBailModalType } from '~/components/Dsdp/DsdpBailModal.vue';
import { ROUTE_NAMES } from '~/constants/routes';
import { isDirectSaleItemEntityWithCurrentPrince, isDirectSaleWithNegotiations } from '~/types/directSale.type';
import { NotificationDuration } from '~/types/notifications.type';

// PROPS
const { directSale } = defineProps({
    directSale: { type: Object as PropType<DirectSaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { notifyError, notifyGenericError } = useNotification();
const { logError } = useLogs();
const { data: authData, isLoggedIn } = useAuthUtils();
const { goToLoginPage } = useLoginRouteUtils();
const userGatedAction = useUserGatedActionState();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, hasOrder, isSold } = useDirectSaleStatus(toRef(() => directSale));
const { mutateAsync: mutatePlaceOrder, isPending: isOrderPending } = useApiPlaceOrderDirectSale();
const { makeProposal } = useDirectSaleNegotiation(() => directSale);
const route = useRoute();
const localePath = useLocalePath();
const { updateTrackingCompanyMetrics, trackDsNegotiationStartNegotiation, trackDsdpOrderModalClose, trackDsdpBuyButton } = useTracking();
const inspectionExpiredModalState = useModalState<boolean>();
const bailModalState = useModalState<boolean>();
const orderModalState = useModalState<boolean>();
const proposalModalState = useModalState<number>();
const isLoadingModalOpen = ref(false);
const loadingModalTitle = ref('');

// DATA
const bailModalType = ref<DsdpBailModalType>('order');
const isBuyer = computed(() => directSale.sellerCompanyId !== authData.value?.user?.companyId);
const placeOrderInProgress = ref<boolean>(false);
const startNegotiationInProgress = ref<boolean>(false);
const userMeta = computed(() => directSale.userMeta);
const shouldShowInspectionExpiredModal = computed(
    () =>
        !!userMeta.value?.inspectionExpiredExportRestrictions &&
        (isBailNeeded.value || (userMeta.value.isWhitelisted && !directSale?.negotiations?.length))
);
const hasPlaceOrder = computed(() => directSale.computedState === DirectSaleComputedState.NoActiveOrder);

const hasStartNegotiation = computed(() => {
    if (isLoggedIn.value && !isBuyer.value) return false;

    if (isDirectSaleWithNegotiations(directSale)) {
        const lastNegotiation = directSale.negotiations.toSorted((a, b) => {
            const dateA = Date.parse(a.createdAt);
            const dateB = Date.parse(b.createdAt);

            return dateB - dateA;
        })[0];
        if (lastNegotiation.state === NegotiationState.Open) return false;
    }

    return directSale.computedState === DirectSaleComputedState.NoActiveOrder;
});
const hasGoToNegotiation = computed(() => isLoggedIn.value && isDirectSaleWithNegotiations(directSale));
const showSection = computed(
    () => hasRestrictedNotice.value || hasInspectionNotice.value || hasPlaceOrder.value || hasStartNegotiation.value || hasGoToNegotiation.value
);

// HANDLERS
const placeOrderFlow = async (): Promise<void> => {
    bailModalType.value = 'order';
    placeOrderInProgress.value = true;
    loadingModalTitle.value = tt('sdp.DirectSale.orderInProgressModal.title');
    trackDsdpBuyButton();

    await (async (): Promise<void> => {
        if (!isLoggedIn.value) {
            userGatedAction.value = { action: UserGatedAction.DSDP_PLACE_ORDER };
            goToLoginPage();

            return;
        }

        if (shouldShowInspectionExpiredModal.value) {
            if (!(await inspectionExpiredModalState.open())) return;
        }

        if (isBailNeeded.value) {
            if (!(await bailModalState.open())) return;

            isLoadingModalOpen.value = true;
            await until(isBailNeeded).toBe(false);
        } else {
            if (!(await orderModalState.open())) {
                trackDsdpOrderModalClose();

                return;
            }
        }

        isLoadingModalOpen.value = true;

        if (!directSale.userMeta?.canBuy) {
            // Error theoretically managed by another flow (e.g. concurrent purchase)
            return;
        }

        try {
            await placeOrderApi();
            await until(isSold).toBe(true);
            updateTrackingCompanyMetrics();
        } catch {
            isLoadingModalOpen.value = false;
        }
        // BSD-2821 Loader kept on success to avoid showing the page without loading while navigation is blocked by the destination page’s suspense
    })();

    placeOrderInProgress.value = false;
};

const placeOrderApi = (): Promise<OrderEntity> => {
    const apiPromise = mutatePlaceOrder(
        { data: { directSaleId: directSale.id } },
        {
            onError: (error: ErrorType<BackendErrorEntity>, variables: Object): void => {
                logError('Error placing DirectSale order', { apiError: error.data, apiData: variables });
            },
        }
    );

    apiPromise.catch((apiError: ErrorType<BackendErrorEntity>) => {
        if (!isBackendError(apiError.data)) {
            notifyGenericError();

            return;
        }

        const { error } = apiError.data;

        if ([BackendErrorCode.AuthTokenExpired, BackendErrorCode.AuthTokenNotValid].includes(error.code)) {
            // Errors already handled elsewhere
            return;
        }

        notifyError({
            title: tt(`sdp.DirectSale.order.orderError.title`),
            description: tt(`sdp.DirectSale.order.orderError.description`),
            duration: NotificationDuration.NoTimeout,
        });
    });

    return apiPromise;
};

const startNegotiationFlow = async (): Promise<void> => {
    bailModalType.value = 'negotiation';
    startNegotiationInProgress.value = true;
    loadingModalTitle.value = tt('directSaleNegotiation.firstOfferInProgressModal.title');
    trackDsNegotiationStartNegotiation();

    await (async (): Promise<void> => {
        if (!isLoggedIn.value) {
            userGatedAction.value = { action: UserGatedAction.DSDP_START_NEGOTIATION };
            goToLoginPage();

            return;
        }

        if (hasInspectionNotice.value) {
            if (!(await inspectionExpiredModalState.open())) return;
        }

        const proposalAmount = await proposalModalState.open();
        if (proposalAmount === undefined) return;

        if (isBailNeeded.value) {
            if (!(await bailModalState.open())) return;

            isLoadingModalOpen.value = true;
            await until(isBailNeeded).toBe(false);
        }

        isLoadingModalOpen.value = true;

        if (!directSale.userMeta?.canBuy) {
            // Error theoretically managed by another flow (e.g. concurrent purchase)
            isLoadingModalOpen.value = false;

            return;
        }

        try {
            await makeProposal(proposalAmount);
            await until(hasGoToNegotiation).toBe(true);
            navigateTo(localePath({ name: ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION, params: route.params }));
        } catch {
            isLoadingModalOpen.value = false;
        }
        // BSD-2821 Loader kept on success to avoid showing the page without loading while navigation is blocked by the destination page’s suspense
    })();

    startNegotiationInProgress.value = false;
};

const closeModals = (): void => {
    inspectionExpiredModalState.cancel();
    proposalModalState.cancel();
    bailModalState.cancel();
};

// LIFECYCLE
onMounted(() => {
    if (!isLoggedIn.value) return;

    if (userGatedAction.value?.action === UserGatedAction.DSDP_START_NEGOTIATION) {
        userGatedAction.value = null;
        startNegotiationFlow();
    } else if (userGatedAction.value?.action === UserGatedAction.DSDP_PLACE_ORDER) {
        userGatedAction.value = null;
        placeOrderFlow();
    }
});

// WATCHERS
watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => directSale,
    value => {
        if (hasOrder.value || (!bailModalState.isOpen && !orderModalState.isOpen && !proposalModalState.isOpen)) return;

        if (value.computedState === DirectSaleComputedState.PendingPayment) {
            closeModals();

            notifyError({
                title: tt('notifications.userDirectSaleAlreadyPurchased.title'),
                description: tt('notifications.userDirectSaleAlreadyPurchased.description', {
                    subject: tt('notifications.userDirectSaleAlreadyPurchased.inDirectSale'),
                }),
                duration: NotificationDuration.NoTimeout,
            });
        }

        if (
            [DirectSaleState.Closed, DirectSaleState.Canceled].includes(value.state) &&
            (placeOrderInProgress.value || startNegotiationInProgress.value)
        ) {
            closeModals();

            notifyError({
                title: tt(`sdp.DirectSale.order.expiredError.title`),
                description: tt(`sdp.DirectSale.order.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="directSale" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <UButton
                    v-if="hasPlaceOrder"
                    size="lg"
                    variant="solid"
                    :ui="buttonResponsiveSizeUi('lg', 'xl')"
                    :label="tt('sdp.DirectSale.orderButton')"
                    block
                    :loading="placeOrderInProgress"
                    :disabled="startNegotiationInProgress"
                    @click="placeOrderFlow"
                />

                <BaseIconBgButton
                    v-if="hasStartNegotiation"
                    size="lg"
                    color="secondary"
                    variant="solid"
                    :ui="buttonResponsiveSizeUi('lg', 'xl')"
                    :label="tt('directSaleNegotiation.startNegotiationButton')"
                    block
                    :loading="startNegotiationInProgress"
                    :disabled="placeOrderInProgress"
                    @click="startNegotiationFlow"
                />

                <LazySdpInspectionExpiredModal
                    v-if="shouldShowInspectionExpiredModal"
                    v-model="inspectionExpiredModalState.isOpen"
                    @confirm="inspectionExpiredModalState.confirm(true)"
                />

                <LazyDsdpOrderModal
                    v-if="isDirectSaleItemEntityWithCurrentPrince(directSale)"
                    v-model="orderModalState.isOpen"
                    :direct-sale="directSale"
                    :loading="isOrderPending"
                    @confirm="orderModalState.confirm(true)"
                />

                <DsdpNegotiationProposalModal
                    v-model="proposalModalState.isOpen"
                    :direct-sale="directSale"
                    delegate-make-proposal
                    @confirm-proposal="proposalModalState.confirm"
                />

                <LazyDsdpBailModal
                    v-if="isBailNeeded"
                    v-model="bailModalState.isOpen"
                    :type="bailModalType"
                    :direct-sale="directSale"
                    @success="bailModalState.confirm(true)"
                />
            </template>

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="directSale" />
        </div>
    </div>
    <BaseLoadingModal v-model:open="isLoadingModalOpen" :title="loadingModalTitle" />
</template>
